import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import ImageUploadField from './ImageUploadField';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { slug: '', name: '', short_description: '', long_description: '', categories: [], website_url: '', instagram_url: '', phone: '', email: '', location: '', image_url: '', gallery_urls: [], events_shot: 0, is_featured: false, is_published: true, order: 0 };
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black';
const Field = ({ label, children }) => <div><label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>{children}</div>;

const VendorsPanel = () => {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [current, setCurrent] = useState(null);
  const [message, setMessage] = useState('');
  const [clicks, setClicks] = useState(null);

  const load = async () => {
    try {
      const [v, c] = await Promise.all([api.request('/api/admin/vendors'), fetch(`${API_URL}/api/vendors/categories`).then((r) => r.json())]);
      setVendors(v); setCategories(c || []);
    } catch (e) { setMessage('Failed to load: ' + e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!current.name.trim()) { setMessage('Name is required'); return; }
    try {
      const body = JSON.stringify({ ...current, events_shot: Number(current.events_shot) || 0, order: Number(current.order) || 0 });
      if (current.id) await api.request(`/api/admin/vendors/${current.id}`, { method: 'PUT', body });
      else await api.request('/api/admin/vendors', { method: 'POST', body });
      setMessage('Vendor saved'); setCurrent(null); load();
    } catch (e) { setMessage('Failed to save: ' + e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    await api.request(`/api/admin/vendors/${id}`, { method: 'DELETE' });
    load();
  };

  const showClicks = async (v) => {
    const d = await api.request(`/api/admin/vendors/${v.id}/clicks`);
    setClicks({ vendor: v, ...d });
  };

  if (current) {
    const set = (k, val) => setCurrent((c) => ({ ...c, [k]: val }));
    const toggleCat = (c) => set('categories', current.categories.includes(c) ? current.categories.filter((x) => x !== c) : [...current.categories, c]);
    return (
      <div className="space-y-5" data-testid="vendor-editor">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">{current.id ? 'Edit' : 'New'} Vendor</h2>
          <button onClick={() => setCurrent(null)} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
        </div>
        {message && <div className="p-3 rounded bg-gray-50 text-sm">{message}</div>}
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Name *"><input data-testid="vendor-name-input" value={current.name} onChange={(e) => set('name', e.target.value)} className={inputCls} /></Field>
          <Field label="URL slug (auto from name if empty)"><input value={current.slug} onChange={(e) => set('slug', e.target.value)} className={inputCls} /></Field>
          <Field label="Website URL"><input data-testid="vendor-website-input" value={current.website_url} onChange={(e) => set('website_url', e.target.value)} className={inputCls} placeholder="https://" /></Field>
          <Field label="Instagram URL"><input value={current.instagram_url} onChange={(e) => set('instagram_url', e.target.value)} className={inputCls} /></Field>
          <Field label="Phone"><input value={current.phone} onChange={(e) => set('phone', e.target.value)} className={inputCls} /></Field>
          <Field label="Location"><input value={current.location} onChange={(e) => set('location', e.target.value)} className={inputCls} placeholder="Toronto, ON" /></Field>
          <Field label="Weddings shot together"><input type="number" min="0" value={current.events_shot} onChange={(e) => set('events_shot', e.target.value)} className={inputCls} /></Field>
          <Field label="Sort order"><input type="number" value={current.order} onChange={(e) => set('order', e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Categories">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c} type="button" onClick={() => toggleCat(c)} data-testid={`vendor-cat-${c.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className={`px-3 py-1 text-xs rounded-full border ${current.categories.includes(c) ? 'bg-black text-white border-black' : 'border-gray-300 hover:bg-gray-50'}`}>{c}</button>
            ))}
          </div>
        </Field>
        <Field label="Short description (card + meta)"><textarea rows={2} value={current.short_description} onChange={(e) => set('short_description', e.target.value)} className={inputCls} /></Field>
        <Field label="Long description"><textarea rows={5} value={current.long_description} onChange={(e) => set('long_description', e.target.value)} className={inputCls} /></Field>
        <Field label="Cover image"><ImageUploadField value={current.image_url} onChange={(url) => set('image_url', url)} folder="vendors" label="Upload cover" /></Field>
        <div className="flex gap-6 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={current.is_featured} onChange={(e) => set('is_featured', e.target.checked)} /> Featured (Trusted badge, shown first)</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={current.is_published} onChange={(e) => set('is_published', e.target.checked)} /> Published</label>
        </div>
        <button onClick={save} data-testid="vendor-save-btn" className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800">Save Vendor</button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendors-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Trusted Vendors</h2>
          <p className="text-sm text-gray-500 mt-1">Directory at /vendors · outbound clicks are tracked per vendor.</p>
        </div>
        <button onClick={() => setCurrent({ ...EMPTY })} data-testid="vendor-new-btn" className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800">+ New vendor</button>
      </div>
      {message && <div className="p-3 rounded bg-gray-50 text-sm">{message}</div>}
      {clicks && (
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-sm" data-testid="vendor-clicks-panel">
          <div className="flex justify-between"><strong>{clicks.vendor.name} — {clicks.total} outbound click{clicks.total === 1 ? '' : 's'}</strong><button onClick={() => setClicks(null)} className="text-gray-500">Close</button></div>
          <ul className="mt-2 space-y-1 max-h-40 overflow-auto text-xs text-gray-600">
            {clicks.recent.slice(0, 20).map((c, i) => <li key={i}>{new Date(c.at).toLocaleString()} · {c.target}{c.referrer ? ` · from ${c.referrer.replace(/^https?:\/\//, '')}` : ''}</li>)}
            {clicks.recent.length === 0 && <li>No clicks yet</li>}
          </ul>
        </div>
      )}
      <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
        {vendors.map((v) => (
          <div key={v.id} className="p-4 flex items-center gap-4" data-testid={`vendor-row-${v.slug}`}>
            <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden shrink-0">{v.image_url && <img src={v.image_url} alt="" className="w-full h-full object-cover" />}</div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-900 truncate">{v.name} {v.is_featured && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-600">featured</span>}{!v.is_published && <span className="ml-1 text-[10px] uppercase tracking-wider text-gray-400">draft</span>}</p>
              <p className="text-xs text-gray-500 truncate">{v.categories?.join(', ')} · <a href={`/vendors/${v.slug}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">/vendors/{v.slug}</a></p>
            </div>
            <button onClick={() => showClicks(v)} className="text-xs text-gray-600 hover:text-black shrink-0" data-testid={`vendor-clicks-${v.slug}`}>{v.click_count || 0} clicks</button>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setCurrent({ ...EMPTY, ...v })} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Edit</button>
              <button onClick={() => remove(v.id)} className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50">Delete</button>
            </div>
          </div>
        ))}
        {vendors.length === 0 && <p className="p-8 text-center text-gray-500 text-sm">No vendors yet.</p>}
      </div>
    </div>
  );
};

export default VendorsPanel;
